// نمای مشترک صورت‌وضعیت (SCR-STAFF-804 و پرتال SCR-STAFF-807): خلاصه (ناخالص، تضمین، سقف، کسور، خالص، پرداخت‌شده، قابل پرداخت، انتقالی)،
// اقلام با علامت «معترض»، کسور، اعتراض‌ها و پرداخت‌ها. در پرتال اقلام برای اعتراض انتخاب‌پذیرند (US-CMP-043).
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { CmpStatementDisputeView, CmpStatementLineView, CmpStatementView } from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  faDigits,
  JalaliDate,
  KeyValueList,
  Money,
  StatusChip,
  type DataTableColumn,
} from '@phoenix/ui';
import type { ReactNode } from 'react';
import { statementTone } from './common';

export function StatementDetail({
  view,
  selected,
  onToggle,
  disputeActions,
}: {
  view: CmpStatementView;
  /** شناسه اقلام انتخاب‌شده برای اعتراض (فقط پرتال). */
  selected?: readonly string[];
  onToggle?: (lineId: string) => void;
  disputeActions?: (dispute: CmpStatementDisputeView) => ReactNode;
}) {
  const { t } = useTranslation('cmp');
  const { summary } = view;
  const money = (value: { amount: number | string }, testId?: string) => (
    <span data-testid={testId}>
      <Money amountMinor={Number(value.amount)} />
    </span>
  );

  const lineColumns: DataTableColumn<CmpStatementLineView>[] = [
    ...(onToggle
      ? [
          {
            id: 'select',
            headerKey: 'cmp:statements.columns.select',
            cell: (row: CmpStatementLineView) => (
              <Checkbox
                size="small"
                checked={selected?.includes(row.id) ?? false}
                disabled={row.disputed}
                onChange={() => onToggle(row.id)}
                slotProps={{
                  input: {
                    'aria-label': t('cmp:statements.select_line', { service: row.serviceName ?? '' }),
                  },
                }}
              />
            ),
          } satisfies DataTableColumn<CmpStatementLineView>,
        ]
      : []),
    {
      id: 'date',
      headerKey: 'cmp:ledger.columns.date',
      cell: (row) => <JalaliDate value={row.businessDate} />,
    },
    {
      id: 'kind',
      headerKey: 'cmp:ledger.columns.kind',
      cell: (row) => t(`cmp:statement_line_kind.${row.kind}`),
    },
    {
      id: 'service',
      headerKey: 'cmp:ledger.columns.service',
      cell: (row) => row.serviceName ?? row.reason ?? '—',
    },
    { id: 'patient', headerKey: 'cmp:statements.columns.patient', cell: (row) => row.patientName ?? '—' },
    {
      id: 'base',
      headerKey: 'cmp:ledger.columns.base',
      cell: (row) => <Money amountMinor={Number(row.base.amount)} />,
      align: 'right',
    },
    {
      id: 'amount',
      headerKey: 'cmp:ledger.columns.amount',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          {row.disputed && (
            <Chip
              size="small"
              color="error"
              label={t('cmp:statements.disputed')}
              data-testid={`statement-line-disputed-${row.id}`}
            />
          )}
          <Money amountMinor={Number(row.amount.amount)} />
        </Stack>
      ),
      align: 'right',
    },
  ];

  return (
    <Stack spacing={2} data-testid="statement-detail">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle1">{summary.beneficiaryName ?? '—'}</Typography>
        <StatusChip
          tone={statementTone(summary.status)}
          labelKey={`cmp:statement_status.${summary.status}`}
        />
      </Stack>
      <KeyValueList
        columns={3}
        items={[
          {
            labelKey: 'cmp:statements.period',
            value: (
              <>
                <JalaliDate value={summary.periodFrom} /> – <JalaliDate value={summary.periodTo} />
              </>
            ),
          },
          { labelKey: 'cmp:statements.gross', value: money(summary.gross, 'statement-gross') },
          {
            labelKey: 'cmp:statements.guarantee',
            value: money(summary.guaranteeTopUp, 'statement-guarantee'),
          },
          { labelKey: 'cmp:statements.cap', value: money(summary.capReduction) },
          { labelKey: 'cmp:statements.deductions', value: money(summary.deductions, 'statement-deductions') },
          { labelKey: 'cmp:statements.net', value: money(summary.net, 'statement-net') },
          { labelKey: 'cmp:statements.paid', value: money(summary.paid, 'statement-paid') },
          { labelKey: 'cmp:statements.payable', value: money(summary.payable, 'statement-payable') },
          { labelKey: 'cmp:statements.carry_forward', value: money(summary.carryForward) },
          ...(summary.disputeDeadline
            ? [
                {
                  labelKey: 'cmp:statements.dispute_deadline',
                  value: <JalaliDate value={summary.disputeDeadline} withTime />,
                },
              ]
            : []),
        ]}
      />
      <DataTable
        captionKey="cmp:statements.lines"
        columns={lineColumns}
        rows={view.lines}
        getRowId={(row) => row.id}
        emptyTitleKey="cmp:statements.no_lines"
      />
      {view.deductions.length > 0 && (
        <Stack spacing={0.5} data-testid="statement-deductions-list">
          <Typography variant="subtitle2">{t('cmp:statements.deductions')}</Typography>
          {view.deductions.map((row) => (
            <Stack key={row.id} direction="row" justifyContent="space-between">
              <span>
                {t(`cmp:deduction_kind.${row.kind}`)}
                {row.title ? ` · ${row.title}` : ''}
              </span>
              <Money amountMinor={Number(row.amount.amount)} />
            </Stack>
          ))}
        </Stack>
      )}
      {view.disputes.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">{t('cmp:statements.disputes')}</Typography>
          {view.disputes.map((dispute) => (
            <Stack
              key={dispute.id}
              spacing={0.5}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
              data-testid={`statement-dispute-${dispute.id}`}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <StatusChip
                  tone={dispute.status === 'Open' ? 'error' : 'inactive'}
                  labelKey={`cmp:dispute_status.${dispute.status}`}
                />
                <Typography variant="body2">
                  {t('cmp:statements.dispute_lines', {
                    count: dispute.lineIds.length,
                    value: faDigits(dispute.lineIds.length),
                  })}{' '}
                  · <Money amountMinor={Number(dispute.amount.amount)} />
                </Typography>
              </Stack>
              <Typography variant="body2">{dispute.comment}</Typography>
              {dispute.resolution && (
                <Typography variant="body2" color="text.secondary">
                  {t('cmp:statements.resolution')}: {dispute.resolution}
                </Typography>
              )}
              {disputeActions?.(dispute)}
            </Stack>
          ))}
        </Stack>
      )}
      {view.payouts.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">{t('cmp:statements.payouts')}</Typography>
          {view.payouts.map((payout) => (
            <Stack key={payout.id} direction="row" justifyContent="space-between">
              <span>
                {t(`cmp:settlement.${payout.method}`)} · <JalaliDate value={payout.businessDate} /> ·{' '}
                {t(`cmp:payout_status.${payout.status}`)}
              </span>
              <Money amountMinor={Number(payout.amount.amount)} />
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
